/**
 * 入札追加タブの入札一覧に固有の絞り込み (AC-BIN-018 のステータス・ワークフロー・コマと、
 * AC-BIN-046 の「売買逆転のみ表示」)。 他のタブとは共有しないが、 選択は AC-SC-031 に従って
 * localStorage に保持し、 タブの切り替え・他の画面への移動・再読み込みのあとに復元する
 * (#12636)。 全体フィルター (受渡日・エリア・売買区分。 AC-BIN-102) とは別の key で持つ。
 */
import { KOMA_COUNT } from '~/constants/koma'
import {
  bool,
  defineFilterCodec,
  nonEmptyText,
  oneOf,
  setOf,
} from '~/models/filterCodec'

/** ステータスの絞り込み。 `''` は「すべて」。 */
export type StatusFilter =
  | ''
  | 'placed'
  | 'matched'
  | 'cancelled'
  | 'autoCancelled'

export const STATUS_FILTER_VALUES: readonly StatusFilter[] = [
  '',
  'placed',
  'matched',
  'autoCancelled',
  'cancelled',
]

/**
 * ワークフローの選択肢の「ワークフローなし」 (= ワークフローを経由せずに作った札、 workflowId 未設定)。
 * 実際の workflowId (ワークフローの業務ID) と衝突しない値として固定文字列を使う。
 */
export const NO_WORKFLOW = '__no_workflow__'

/**
 * 空の Set は「すべて」 (絞り込みなし)。 `workflows` は札の workflowId (業務ID) と
 * `NO_WORKFLOW`、 `komas` はコマ番号の文字列 (MultiSelect の value 型)。
 */
export type IntradayBidListFilters = {
  status: StatusFilter
  workflows: Set<string>
  komas: Set<string>
  inversionOnly: boolean
}

export const INTRADAY_BID_LIST_FILTERS_STORAGE_KEY =
  'ebalance-intraday-bid-list-filters'

const KOMA_VALUES = Array.from({ length: KOMA_COUNT }, (_, i) => String(i + 1))

export function defaultIntradayBidListFilters(): IntradayBidListFilters {
  return {
    status: '',
    workflows: new Set(),
    komas: new Set(),
    inversionOnly: false,
  }
}

export const intradayBidListFiltersCodec =
  defineFilterCodec<IntradayBidListFilters>({
    defaults: defaultIntradayBidListFilters,
    fields: {
      status: oneOf<StatusFilter>(STATUS_FILTER_VALUES),
      workflows: setOf(nonEmptyText()),
      komas: setOf(oneOf<string>(KOMA_VALUES)),
      inversionOnly: bool(),
    },
  })

/**
 * 選んでいるワークフローのうち、 受渡日の札に無いもの (`workflowOptions` に無いもの) を外す。
 * 他の日に選んだワークフローを復元すると一覧が 0 件に見えるため。 「ワークフローなし」 と
 * 開いているワークフロー (`openedWorkflowId`。 まだ札が無くても、 これから作る札を隠さない) は常に残し、
 * 全部外れたら「すべて」。 外すものが無ければ undefined (呼び出し側は保存しない)。
 */
export function pruneUnknownWorkflows(
  filters: IntradayBidListFilters,
  workflowOptions: readonly string[],
  openedWorkflowId?: string,
): IntradayBidListFilters | undefined {
  const known = new Set(workflowOptions)
  const kept = new Set(
    [...filters.workflows].filter(
      (id) => id === NO_WORKFLOW || id === openedWorkflowId || known.has(id),
    ),
  )
  if (kept.size === filters.workflows.size) return undefined
  return { ...filters, workflows: kept }
}

/**
 * 入札一覧の絞り込みを 1 回だけ広げる契機。 `csvImport` は入札 CSV を取り込んで移ってきた時
 * (`komas` は取り込んだ札のコマ、 `workflowId` は開いているワークフローの業務ID)、 `workflow` は
 * ワークフローから開いた時。
 */
export type BidListFilterLanding =
  | {
      kind: 'csvImport'
      komas: readonly number[]
      workflowId: string | undefined
    }
  | { kind: 'workflow'; workflowId: string }

/**
 * 入札 CSV を取り込んで移ってきた時に、 取り込んだ札が一覧に出るよう絞り込みを広げる
 * (AC-BCI-038: 今の選択に足し、 選択を狭めない)。 ステータスは単一選択で下書きの選択肢が無いので
 * 「すべて」、 「売買逆転のみ表示」 は外す。 コマ・ワークフローは選んでいる時だけ取り込んだ分を足す
 * (ワークフローから開いている時は、 取り込んだ札がそのワークフローの ID を持ちうる。 AC-WFW-047)。
 */
export function widenForCsvImport(
  filters: IntradayBidListFilters,
  imported: { komas: readonly number[]; workflowId: string | undefined },
): IntradayBidListFilters {
  const komas =
    filters.komas.size === 0
      ? filters.komas
      : new Set([...filters.komas, ...imported.komas.map(String)])
  const workflows =
    filters.workflows.size === 0
      ? filters.workflows
      : new Set([
          ...filters.workflows,
          NO_WORKFLOW,
          ...(imported.workflowId === undefined ? [] : [imported.workflowId]),
        ])
  return { status: '', komas, workflows, inversionOnly: false }
}

/**
 * ワークフローから開いた時、 ワークフローを選んでいてそのワークフローを含まなければ足す
 * (開いたワークフローの札を隠さない)。 「すべて」 か既に含む時は undefined (保存しない)。
 */
export function includeOpenedWorkflow(
  filters: IntradayBidListFilters,
  workflowId: string,
): IntradayBidListFilters | undefined {
  if (filters.workflows.size === 0 || filters.workflows.has(workflowId)) {
    return undefined
  }
  return { ...filters, workflows: new Set([...filters.workflows, workflowId]) }
}
